/**
 * AI Scheduler Store
 * 
 * Manages state for the AI Scheduler service, including:
 * - Service authentication state
 * - Tenant discovery and selection
 * - AI proposal creation and management
 * - Human-in-the-loop approval workflow
 */

import { defineStore } from 'pinia'
import aiSchedulerApi from '../services/aiSchedulerApi.js'
import { useServiceAuthStore } from './serviceAuth.js'

export const useAISchedulerStore = defineStore('aiScheduler', {
  state: () => ({
    // Service authentication
    serviceAuth: null,
    
    // Tenant management
    tenants: [],
    selectedTenant: null,
    tenantLoading: false,
    tenantError: null,
    
    // Proposal management
    proposals: [],
    selectedProposal: null,
    proposalsLoading: false,
    proposalsError: null,
    
    // Generation state
    generation: {
      isGenerating: false,
      progress: 0,
      currentStep: '',
      error: null
    },
    
    // Analytics
    analytics: {
      totalProposals: 0,
      byStatus: {
        pending: 0,
        approved: 0,
        rejected: 0,
        committed: 0
      },
      avgConfidenceScore: 0.0,
      acceptanceRate: 0.0
    },
    
    // UI state
    ui: {
      showTenantSelector: false,
      showProposalModal: false,
      showAnalytics: false,
      selectedView: 'dashboard' // dashboard, tenants, proposals, analytics
    }
  }),

  getters: {
    // Service authentication getters
    isServiceAuthenticated: (state) => {
      return state.serviceAuth?.isAuthenticated || false
    },
    
    serviceScopes: (state) => {
      return state.serviceAuth?.scopes || []
    },
    
    hasScope: (state) => {
      return (scope) => state.serviceAuth?.hasScope?.(scope) || false
    },
    
    // Tenant getters
    activeTenants: (state) => {
      return state.tenants.filter(tenant => 
        tenant.billing_status === 'active' && tenant.allowed_for_ai !== false
      )
    },
    
    tenantById: (state) => {
      return (tenantId) => state.tenants.find(t => t.id === tenantId || t.schema_name === tenantId)
    },
    
    // Proposal getters
    proposalsByStatus: (state) => {
      const grouped = {}
      state.proposals.forEach(proposal => {
        const status = proposal.status || 'pending'
        if (!grouped[status]) grouped[status] = []
        grouped[status].push(proposal)
      })
      return grouped
    },
    
    pendingProposals: (state) => {
      return state.proposals.filter(p => p.status === 'pending')
    },
    
    recentProposals: (state) => {
      return state.proposals
        .slice()
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 10)
    },
    
    // Analytics getters
    totalActiveProposals: (state) => {
      return state.proposals.filter(p => 
        ['pending', 'reviewing', 'approved'].includes(p.status)
      ).length
    },
    
    averageConfidenceScore: (state) => {
      const scores = state.proposals.map(p => p.confidence_score || 0)
      return scores.length > 0 
        ? scores.reduce((sum, score) => sum + score, 0) / scores.length 
        : 0
    }
  },

  actions: {
    // ============================================
    // SERVICE AUTHENTICATION
    // ============================================
    
    async initializeService() {
      try {
        // Get service auth store instance
        this.serviceAuth = useServiceAuthStore()
        
        // Initialize authentication
        await this.serviceAuth.initialize()
        
        return true
      } catch (error) {
        throw error
      }
    },

    // ============================================
    // TENANT MANAGEMENT
    // ============================================
    
    async loadTenants(filters = {}) {
      this.tenantLoading = true
      this.tenantError = null
      
      try {
        // Set default filters for AI scheduling
        const aiFilters = {
          allowed_for_ai: true,
          billing_status: 'active',
          ...filters
        }
        
        this.tenants = await aiSchedulerApi.getTenants(aiFilters)
        
        return this.tenants
      } catch (error) {
        this.tenantError = error.message
        console.error('Failed to load tenants:', error)
        throw error
      } finally {
        this.tenantLoading = false
      }
    },

    async selectTenant(tenantId) {
      try {
        // Load tenant details if not already loaded
        let tenant = this.tenantById(tenantId)
        if (!tenant) {
          tenant = await aiSchedulerApi.getTenant(tenantId)
          // Add to tenants list if not exists
          if (!this.tenants.find(t => t.id === tenant.id)) {
            this.tenants.push(tenant)
          }
        }
        
        this.selectedTenant = tenant
        
        // Load proposals for this tenant
        await this.loadProposals({ tenant_id: tenantId })
        
        return tenant
      } catch (error) {
        this.tenantError = error.message
        throw error
      }
    },

    clearSelectedTenant() {
      this.selectedTenant = null
      this.proposals = []
      this.selectedProposal = null
    },

    // ============================================
    // PROPOSAL MANAGEMENT
    // ============================================
    
    async loadProposals(filters = {}) {
      this.proposalsLoading = true
      this.proposalsError = null
      
      try {
        const allProposals = await aiSchedulerApi.getProposals(filters)
        
        // Filter out rejected proposals - they should not be shown in the UI
        this.proposals = allProposals.filter(p => p.status !== 'rejected')
        
        return this.proposals
      } catch (error) {
        this.proposalsError = error.message
        console.error('Failed to load proposals:', error)
        throw error
      } finally {
        this.proposalsLoading = false
      }
    },

    async createProposal(proposalData) {
      try {
        const proposal = await aiSchedulerApi.createProposal(proposalData)
        
        // Add to proposals list
        this.proposals.unshift(proposal)
        
        return proposal
      } catch (error) {
        this.proposalsError = error.message
        console.error('Failed to create proposal:', error)
        throw error
      }
    },

    async generateProposal(tenantId, startDate, endDate, options = {}) {
      this.generation.isGenerating = true
      this.generation.progress = 0
      this.generation.currentStep = 'Initializing...'
      this.generation.error = null
      
      try {
        this.generation.currentStep = 'Loading tenant data...'
        this.generation.progress = 20
        
        // Add artificial delay for better UX
        await new Promise(resolve => setTimeout(resolve, 1000))
        
        this.generation.currentStep = 'Analyzing constraints...'
        this.generation.progress = 40
        
        await new Promise(resolve => setTimeout(resolve, 1000))
        
        this.generation.currentStep = 'Generating schedule...'
        this.generation.progress = 60
        
        // Generate the actual proposal
        const proposal = await aiSchedulerApi.generateScheduleProposal(
          tenantId, 
          startDate, 
          endDate, 
          options
        )
        
        this.generation.currentStep = 'Finalizing proposal...'
        this.generation.progress = 80
        
        await new Promise(resolve => setTimeout(resolve, 500))
        
        // Add to proposals list
        this.proposals.unshift(proposal)
        
        this.generation.progress = 100
        this.generation.currentStep = 'Complete!'
        
        // Clear generation state after a short delay
        setTimeout(() => {
          this.generation.isGenerating = false
          this.generation.progress = 0
          this.generation.currentStep = ''
        }, 1000)
        
        return proposal
      } catch (error) {
        this.generation.error = error.message
        this.generation.isGenerating = false
        this.proposalsError = error.message
        console.error('Failed to generate proposal:', error)
        throw error
      }
    },

    // ============================================
    // CLAUDE AI INTEGRATION
    // ============================================

    async checkClaudeStatus() {
      try {
        const status = await aiSchedulerApi.getClaudeStatus()
        return status
      } catch (error) {
        console.error('Failed to check Claude AI status:', error)
        throw error
      }
    },

    async updateProposalStatus(proposalId, status, reviewNotes = '', modifiedShifts = null) {
      try {
        const updatedProposal = await aiSchedulerApi.updateProposalStatus(
          proposalId,
          status,
          reviewNotes,
          modifiedShifts
        )
        
        // Update in proposals list
        const index = this.proposals.findIndex(p => p.id === proposalId)
        if (index !== -1) {
          this.proposals[index] = { ...this.proposals[index], ...updatedProposal }
        }
        
        // Update selected proposal if it's the same one
        if (this.selectedProposal?.id === proposalId) {
          this.selectedProposal = { ...this.selectedProposal, ...updatedProposal }
        }
        
        return updatedProposal
      } catch (error) {
        this.proposalsError = error.message
        throw error
      }
    },

    async approveProposal(proposalId, reviewNotes = '') {
      return await this.updateProposalStatus(proposalId, 'approved', reviewNotes)
    },

    async rejectProposal(proposalId, reviewNotes = '') {
      try {
        // Delete the proposal from the backend instead of just marking as rejected
        await aiSchedulerApi.deleteProposal(proposalId, reviewNotes)
        
        // Remove from local state
        this.proposals = this.proposals.filter(p => p.id !== proposalId)
        
        // Clear selected proposal if it's the one being rejected
        if (this.selectedProposal?.id === proposalId) {
          this.selectedProposal = null
        }
        return {
          id: proposalId,
          deleted: true,
          reason: reviewNotes
        }
      } catch (error) {
        this.proposalsError = error.message
        console.error('Failed to delete rejected proposal:', error)
        throw error
      }
    },

    async requestChanges(proposalId, reviewNotes = '') {
      if (!reviewNotes || reviewNotes.trim() === '') {
        throw new Error('Feedback is required to request changes. Please provide specific details about what should be improved.')
      }

      try {
        this.generation.isGenerating = true
        this.generation.progress = 0
        this.generation.currentStep = 'Sending your feedback to AI...'
        this.generation.error = null

        // First, mark the original proposal as under review
        await this.updateProposalStatus(proposalId, 'reviewing', reviewNotes)
        
        this.generation.progress = 20
        this.generation.currentStep = 'AI analyzing your feedback...'
        
        // Regenerate the proposal with the feedback
        const regeneratedProposal = await aiSchedulerApi.regenerateProposalWithFeedback(
          proposalId,
          reviewNotes,
          { method: 'claude' }
        )
        
        this.generation.progress = 80
        this.generation.currentStep = 'Processing new proposal...'
        
        // Add the new proposal to the list
        const existingIndex = this.proposals.findIndex(p => p.id === regeneratedProposal.id)
        if (existingIndex >= 0) {
          this.proposals[existingIndex] = regeneratedProposal
        } else {
          this.proposals.unshift(regeneratedProposal)
        }
        
        // Update selected proposal to the new one
        this.selectedProposal = regeneratedProposal
        
        this.generation.progress = 100
        this.generation.currentStep = 'Complete!'
        this.generation.isGenerating = false
        
        return regeneratedProposal
      } catch (error) {
        this.generation.error = error.message
        this.generation.isGenerating = false
        console.error('Failed to regenerate proposal with feedback:', error)
        throw error
      }
    },

    selectProposal(proposal) {
      this.selectedProposal = proposal
    },

    clearSelectedProposal() {
      this.selectedProposal = null
    },

    // ============================================
    // ANALYTICS
    // ============================================
    
    async loadAnalytics(filters = {}) {
      try {
        const analyticsData = await aiSchedulerApi.getProposalAnalytics(filters)
        
        this.analytics = {
          ...this.analytics,
          ...analyticsData
        }
        
        return this.analytics
      } catch (error) {
        console.error('Failed to load analytics:', error)
        // Don't throw - analytics are non-critical
        return this.analytics
      }
    },

    // ============================================
    // UI STATE MANAGEMENT
    // ============================================
    
    setView(viewName) {
      this.ui.selectedView = viewName
    },

    toggleTenantSelector() {
      this.ui.showTenantSelector = !this.ui.showTenantSelector
    },

    toggleProposalModal() {
      this.ui.showProposalModal = !this.ui.showProposalModal
    },

    toggleAnalytics() {
      this.ui.showAnalytics = !this.ui.showAnalytics
    },

    // ============================================
    // ERROR HANDLING
    // ============================================
    
    clearTenantError() {
      this.tenantError = null
    },

    clearProposalsError() {
      this.proposalsError = null
    },

    clearGenerationError() {
      this.generation.error = null
    },

    clearAllErrors() {
      this.tenantError = null
      this.proposalsError = null
      this.generation.error = null
    },

    // ============================================
    // ML MODEL TRAINING
    // ============================================

    /**
     * Record that a schedule has been reviewed
     * Makes the review available for ML training
     */
    async recordScheduleReview(proposalId) {
      try {
        return await aiSchedulerApi.recordScheduleReview(proposalId)
      } catch (error) {
        console.error('Failed to record schedule review:', error)
        throw error
      }
    },

    /**
     * Train ML model on human-reviewed schedules
     * Returns training results
     */
    async trainMLModel(epochs = 50) {
      try {
        return await aiSchedulerApi.trainMLModel(epochs)
      } catch (error) {
        console.error('Failed to train ML model:', error)
        throw error
      }
    }
  }
})